import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import type { DurationUnitType } from "dayjs/plugin/duration";

dayjs.extend(duration);

export interface DurationFormatOptions {
  style?: "compact" | "long" | "human";
  maxParts?: number;
  includeSeconds?: boolean;
  unit?: string;
  toUnit?: string;
  type?: string;
}

const durationSuffixes = {
  years: "y",
  months: "mo",
  days: "d",
  hours: "h",
  minutes: "min",
  seconds: "s",
} as const;

/**
 * Formats a duration value into a human-readable string
 * @param value Duration value in the specified unit (default: milliseconds)
 * @param options Formatting options including input unit
 * @returns Formatted duration string
 */
export function formatDuration(
  value: number | string,
  options: DurationFormatOptions = {}
): string {
  const {
    style = "compact",
    maxParts = 2,
    includeSeconds = true,
    unit,
    toUnit,
    type,
  } = options;

  // Convert string to number if needed
  let ms = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isNaN(ms)) {
    return value.toString();
  } else if (ms === 0) {
    return "0";
  }

  // Convert from input unit to milliseconds if unit is specified
  if (unit && unit !== "millisecond") {
    ms = convertToMilliseconds(ms, unit);
  }

  // If specific target unit is specified, use unit-specific formatting
  if (toUnit) {
    return formatWithSpecificUnits(ms, "millisecond", toUnit);
  }

  const dur = dayjs.duration(ms);

  if (style === "human") {
    // Human readable format like "2 months" or "3 days"
    // Use more granular thresholds to avoid rounding issues
    if (dur.asHours() < 1) {
      if (dur.minutes() > 0) {
        return `${dur.minutes()} minutes`;
      } else if (dur.seconds() > 1) {
        return `${dur.seconds()} seconds`;
      } else {
        return "a few seconds";
      }
    }
    if (dur.asDays() < 1) {
      return `${dur.hours()} hours`;
    }
    if (dur.asDays() < 30) {
      return `${Math.floor(dur.asDays())} days`;
    }
    if (dur.asDays() < 365) {
      const months = Math.floor(dur.asDays() / 30);
      return `${months} month${months === 1 ? "" : "s"}`;
    }
    const years = Math.floor(dur.asDays() / 365);
    return `${years} year${years === 1 ? "" : "s"}`;
  }

  if (style === "long") {
    // Long format like "2 days 3 hours 45 minutes"
    const parts = [];
    const days = Math.floor(dur.asDays());
    if (days > 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
    if (dur.hours() > 0)
      parts.push(`${dur.hours()} hour${dur.hours() === 1 ? "" : "s"}`);
    if (dur.minutes() > 0)
      parts.push(`${dur.minutes()} minute${dur.minutes() === 1 ? "" : "s"}`);
    if ((includeSeconds && dur.seconds() > 0) || parts.length === 0) {
      parts.push(`${dur.seconds()} second${dur.seconds() === 1 ? "" : "s"}`);
    }
    return parts.slice(0, maxParts).join(" ");
  }

  // Compact format like "2d 3h 45m"
  const parts = [];
  const days = Math.floor(dur.asDays());
  if (days > 0) parts.push(`${days}d`);
  if (dur.hours() > 0) parts.push(`${dur.hours()}h`);
  if (dur.minutes() > 0) parts.push(`${dur.minutes()}m`);
  if ((includeSeconds && dur.seconds() > 0) || parts.length === 0) {
    parts.push(`${dur.seconds()}s`);
  }
  return parts.slice(0, maxParts).join(" ");
}

/**
 * Helper to convert a value from a specific unit to milliseconds
 */
function convertToMilliseconds(value: number, fromUnit: string): number {
  switch (fromUnit) {
    case "second": {
      return value * 1000;
    }
    case "minute": {
      return value * 60 * 1000;
    }
    case "hour": {
      return value * 60 * 60 * 1000;
    }
    case "day": {
      return value * 24 * 60 * 60 * 1000;
    }
    case "week": {
      return value * 7 * 24 * 60 * 60 * 1000;
    }
    case "month": {
      // Approximate
      return value * 30 * 24 * 60 * 60 * 1000;
    }
    case "year": {
      // Approximate
      return value * 365 * 24 * 60 * 60 * 1000;
    }
    default: {
      return value; // Assume milliseconds if unknown unit
    }
  }
}

/**
 * Helper to convert a value from a specific unit to seconds
 */
function convertToSeconds(value: number, fromUnit?: string): number {
  if (!fromUnit) return value; // Assume already in seconds

  switch (fromUnit) {
    case "millisecond": {
      return value / 1000;
    }
    case "minute": {
      return value * 60;
    }
    case "hour": {
      return value * 60 * 60;
    }
    case "day": {
      return value * 24 * 60 * 60;
    }
    case "week": {
      return value * 7 * 24 * 60 * 60;
    }
    case "month": {
      // Approximate
      return value * 30 * 24 * 60 * 60;
    }
    case "year": {
      // Approximate
      return value * 365 * 24 * 60 * 60;
    }
    default: {
      return value; // Assume seconds if unknown unit
    }
  }
}

/**
 * Format a duration with specific input and target units
 * Replacement for humanizeDuration with better integration
 */
export function formatWithSpecificUnits(
  value: number,
  inUnit: string,
  toUnit?: string
): string {
  if (value === 0) return "0";

  if (
    toUnit &&
    (["days", "hours", "minutes", "months"] as string[]).includes(toUnit)
  ) {
    // Convert explicitly to the target unit when specified
    const now = dayjs();
    const later = now.add(Math.abs(value), inUnit as DurationUnitType);
    const diff = later.diff(now, toUnit as DurationUnitType);
    return `${value < 0 ? "-" : ""}${diff}${
      durationSuffixes[toUnit as keyof typeof durationSuffixes]
    }`;
  } else {
    // Generate compact human-readable duration with the appropriate units
    const duration = dayjs.duration(
      Math.abs(value),
      inUnit as DurationUnitType
    );
    const units = Object.keys(durationSuffixes);
    const vals = units.map((u) => (duration as any)[u]());
    const res = [];
    let idx = 0;
    let end = vals.length;

    // Find the first non-zero value and include up to 2 units
    while (idx < end) {
      const val = vals[idx];
      if (val > 0) {
        const suffix =
          durationSuffixes[units[idx] as keyof typeof durationSuffixes];
        res.push(`${val}${suffix}`);
        end = Math.min(end, idx + 2); // Limit to the first two significant units
      }
      idx += 1;
    }

    if (res.length === 0) {
      if (toUnit) {
        res.push(
          `0${durationSuffixes[toUnit as keyof typeof durationSuffixes]}`
        );
      } else {
        res.push("0s");
      }
    }

    const humanized = res.join("");
    return value < 0 ? `-${humanized}` : humanized;
  }
}

/**
 * Format business duration in seconds
 * Business time is calculated based on 9 hour workdays and 5 day workweeks
 */
export function formatBusinessDuration(seconds: number): string {
  const businessWeeks = Math.floor(seconds / (3600 * 9 * 5));
  let remaining = seconds - businessWeeks * 3600 * 9 * 5;
  const businessDays = Math.floor(remaining / (3600 * 9));
  remaining -= businessDays * 3600 * 9;
  const hours = Math.floor(remaining / 3600);
  remaining -= hours * 3600;
  const minutes = Math.floor(remaining / 60);

  const parts = [
    { qty: businessWeeks, label: "bus.week" },
    { qty: businessDays, label: "bus.day" },
    { qty: hours, label: "h" },
    { qty: minutes, label: "min" },
  ]
    .filter((p) => p.qty > 0)
    .slice(0, 2);

  return parts.length > 0
    ? parts.map((p) => `${p.qty}${p.label}`).join(" ")
    : "0min";
}
